// ============================================================================
// RELATÓRIO DE EVASÃO - DASHBOARD ADMIN
// ============================================================================
// Arquivo: src/components/Dashboard_ADM/RelatorioEvasao.tsx
// UPDATED: 2026-03-25 — v3.1
// FIXES: fetch + authH() → apiClient (JWT automático)
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AlertTriangle, Shield, CheckCircle, XCircle, Eye, RefreshCw,
  Search, Filter, Loader2, Clock, Users, Building2, Zap,
  ChevronDown, X, Play, UserSearch, TrendingUp, AlertCircle
} from 'lucide-react';
import apiClient from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────
type EvasionType     = 'duplicate_registration' | 'multiple_affiliates' | 'suspicious_activity' | 'document_fraud';
type EvasionStatus   = 'detected' | 'confirmed' | 'resolved' | 'false_positive';
type SeverityLevel   = 'low' | 'medium' | 'high' | 'critical';

interface Evasion {
  id: string;
  studentId: string;
  student?: { fullName?: string; cpf?: string; user?: { email?: string } };
  affiliateFromId: string | null;
  affiliateFrom?: { schoolName?: string };
  affiliateToId: string | null;
  affiliateTo?: { schoolName?: string };
  evasionType: EvasionType;
  status: EvasionStatus;
  description: string | null;
  detectionData: {
    email?: string; cpf?: string; duplicateCount?: number;
    affiliateIds?: string[]; matchFields?: string[]; confidenceScore?: number;
    registrationCount?: number; daysDiff?: number;
  } | null;
  detectedAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolution: string | null;
  isResolved: boolean;
  severityLevel: SeverityLevel;
}

interface EvasionStats {
  total: number; detected: number; confirmed: number;
  resolved: number; falsePositives: number; unresolved: number;
  bySeverity: Array<{ severity: string; count: number }>;
  byType:     Array<{ type: string; count: number }>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const SEV_CONFIG: Record<SeverityLevel, { label: string; color: string; bg: string; border: string; dot: string }> = {
  critical: { label: 'Crítico', color: 'text-red-700',    bg: 'bg-red-100',    border: 'border-red-300',    dot: 'bg-red-600' },
  high:     { label: 'Alto',    color: 'text-orange-700', bg: 'bg-orange-100', border: 'border-orange-300', dot: 'bg-orange-500' },
  medium:   { label: 'Médio',   color: 'text-amber-700',  bg: 'bg-amber-100',  border: 'border-amber-300',  dot: 'bg-amber-500' },
  low:      { label: 'Baixo',   color: 'text-blue-700',   bg: 'bg-blue-100',   border: 'border-blue-300',   dot: 'bg-blue-400' },
};

const TYPE_LABELS: Record<EvasionType, string> = {
  duplicate_registration: 'Cadastro Duplicado (Email)',
  document_fraud:         'Fraude de Documento (CPF)',
  suspicious_activity:    'Atividade Suspeita',
  multiple_affiliates:    'Múltiplas Filiadas',
};

const STATUS_CONFIG: Record<EvasionStatus, { label: string; color: string; bg: string }> = {
  detected:       { label: 'Detectado',      color: 'text-amber-700', bg: 'bg-amber-100' },
  confirmed:      { label: 'Confirmado',     color: 'text-red-700',   bg: 'bg-red-100' },
  resolved:       { label: 'Resolvido',      color: 'text-green-700', bg: 'bg-green-100' },
  false_positive: { label: 'Falso Positivo', color: 'text-gray-700',  bg: 'bg-gray-100' },
};

// ─── Component ────────────────────────────────────────────────────────────────
const RelatorioEvasao: React.FC = () => {
  const [evasions, setEvasions]           = useState<Evasion[]>([]);
  const [stats, setStats]                 = useState<EvasionStats | null>(null);
  const [loading, setLoading]             = useState(true);
  const [loadingStats, setLoadingStats]   = useState(true);
  const [acao, setAcao]                   = useState<{ id: string; tipo: 'resolve' | 'false-positive' } | null>(null);
  const [resolucao, setResolucao]         = useState('');
  const [salvando, setSalvando]           = useState(false);
  const [triggerando, setTriggerando]     = useState(false);
  const [triggerMsg, setTriggerMsg]       = useState('');
  const [detalhe, setDetalhe]             = useState<Evasion | null>(null);
  const [checkStudentId, setCheckStudentId] = useState('');
  const [checkResult, setCheckResult]     = useState<any>(null);
  const [checkLoading, setCheckLoading]   = useState(false);

  const [filtroStatus, setFiltroStatus]     = useState<string>('');
  const [filtroSeverity, setFiltroSeverity] = useState<string>('');
  const [filtroType, setFiltroType]         = useState<string>('');
  const [busca, setBusca]                   = useState('');
  const [abaAtiva, setAbaAtiva]             = useState<'pendentes' | 'todos' | 'criticos'>('pendentes');

  // ─── Fetch ────────────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const data = await apiClient.get<EvasionStats>('/evasion/stats');
      setStats(data);
    } catch { setStats(null); }
    finally { setLoadingStats(false); }
  }, []);

  const fetchEvasions = useCallback(async () => {
    setLoading(true);
    try {
      let endpoint = '/evasion';
      if (abaAtiva === 'pendentes')     endpoint = '/evasion/unresolved';
      else if (abaAtiva === 'criticos') endpoint = '/evasion/critical';
      else {
        const params = new URLSearchParams();
        if (filtroStatus)   params.append('status',   filtroStatus);
        if (filtroSeverity) params.append('severity', filtroSeverity);
        if (filtroType)     params.append('type',     filtroType);
        if (params.toString()) endpoint += '?' + params.toString();
      }
      const data: any = await apiClient.get(endpoint);
      setEvasions(data.evasions ?? []);
    } catch { setEvasions([]); }
    finally { setLoading(false); }
  }, [abaAtiva, filtroStatus, filtroSeverity, filtroType]);

  useEffect(() => { fetchStats();   }, [fetchStats]);
  useEffect(() => { fetchEvasions(); }, [fetchEvasions]);

  // ─── Ações ────────────────────────────────────────────────────────────────
  const handleConfirmar = async (id: string) => {
    setSalvando(true);
    try {
      await apiClient.post(`/evasion/${id}/confirm`, {});
      fetchEvasions(); fetchStats();
    } catch { alert('Erro ao confirmar evasão'); }
    finally { setSalvando(false); }
  };

  const handleResolver = async () => {
    if (!acao) return;
    if (!resolucao.trim()) { alert('Informe a resolução'); return; }
    setSalvando(true);
    try {
      if (acao.tipo === 'resolve') {
        await apiClient.post(`/evasion/${acao.id}/resolve`, { resolution: resolucao });
      } else {
        await apiClient.post(`/evasion/${acao.id}/false-positive`, {});
      }
      setAcao(null); setResolucao('');
      fetchEvasions(); fetchStats();
    } catch { alert('Erro ao processar ação'); }
    finally { setSalvando(false); }
  };

  const handleTrigger = async () => {
    setTriggerando(true); setTriggerMsg('');
    try {
      const data: any = await apiClient.post('/evasion/run-detection', {});
      setTriggerMsg(data.message ?? 'Detecção iniciada!');
      setTimeout(() => { fetchEvasions(); fetchStats(); }, 3000);
    } catch { setTriggerMsg('Erro ao acionar detecção'); }
    finally { setTriggerando(false); }
  };

  const handleCheckStudent = async () => {
    if (!checkStudentId.trim()) { alert('Informe o ID do aluno'); return; }
    setCheckLoading(true); setCheckResult(null);
    try {
      const data = await apiClient.post(`/evasion/check-student/${checkStudentId}`, {});
      setCheckResult(data);
    } catch { alert('Erro ao verificar aluno'); }
    finally { setCheckLoading(false); }
  };

  // ─── Filtro local ─────────────────────────────────────────────────────────
  const evasoesFiltradas = useMemo(() => {
    if (!busca) return evasions;
    const b = busca.toLowerCase();
    return evasions.filter(e =>
      e.student?.fullName?.toLowerCase().includes(b) ||
      e.detectionData?.email?.toLowerCase().includes(b) ||
      e.detectionData?.cpf?.includes(b) ||
      e.affiliateFrom?.schoolName?.toLowerCase().includes(b) ||
      e.affiliateTo?.schoolName?.toLowerCase().includes(b) ||
      e.id.toLowerCase().includes(b)
    );
  }, [evasions, busca]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-1">Relatório de Evasão</h2>
            <p className="text-gray-600">Detecta registros duplicados (email/CPF) entre Filiadas e atividades suspeitas</p>
          </div>
          <div className="flex gap-3">
            <button onClick={fetchEvasions}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
              <RefreshCw className="w-5 h-5" /> Atualizar
            </button>
            <button onClick={handleTrigger} disabled={triggerando}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-600 text-white font-bold hover:shadow-lg disabled:opacity-60">
              {triggerando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
              Rodar Detecção
            </button>
          </div>
        </div>

        {triggerMsg && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-800 font-semibold text-sm flex items-center gap-2">
            <Zap className="w-4 h-4 text-blue-600" /> {triggerMsg}
          </div>
        )}

        {/* Stats */}
        {loadingStats ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : stats ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: 'Total',          value: stats.total,          bg: 'bg-gray-50',   border: 'border-gray-200',   color: 'text-gray-900' },
              { label: 'Não Resolvidos', value: stats.unresolved,     bg: 'bg-red-50',    border: 'border-red-200',    color: 'text-red-700' },
              { label: 'Detectados',     value: stats.detected,       bg: 'bg-amber-50',  border: 'border-amber-200',  color: 'text-amber-700' },
              { label: 'Confirmados',    value: stats.confirmed,      bg: 'bg-orange-50', border: 'border-orange-200', color: 'text-orange-700' },
              { label: 'Resolvidos',     value: stats.resolved,       bg: 'bg-green-50',  border: 'border-green-200',  color: 'text-green-700' },
              { label: 'Falso Positivo', value: stats.falsePositives, bg: 'bg-blue-50',   border: 'border-blue-200',   color: 'text-blue-700' },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-xl p-4 border ${s.border}`}>
                <p className="text-xs text-gray-600 mb-1">{s.label}</p>
                <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* VERIFICAÇÃO MANUAL */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <h3 className="text-base font-black text-gray-900 mb-4 flex items-center gap-2">
          <UserSearch className="w-5 h-5 text-blue-600" /> Verificar Aluno Específico
        </h3>
        <div className="flex gap-3">
          <input value={checkStudentId} onChange={e => setCheckStudentId(e.target.value)}
            className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 font-semibold"
            placeholder="ID do aluno (UUID)..." />
          <button onClick={handleCheckStudent} disabled={checkLoading}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 flex items-center gap-2 disabled:opacity-60">
            {checkLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
            Verificar
          </button>
        </div>
        {checkResult && (
          <div className={`mt-4 p-4 rounded-xl border ${checkResult.hasEvasion ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            {checkResult.hasEvasion ? (
              <div>
                <p className="font-black text-red-900 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" /> Evasão Detectada — {checkResult.duplicates?.length} conflito(s)
                </p>
                {checkResult.duplicates?.map((d: any, i: number) => (
                  <div key={i} className="mt-2 text-sm text-red-800">
                    <span className="font-bold">{d.field?.toUpperCase()}:</span> {d.value} — duplicado em {d.matchingStudents?.length} outro(s)
                  </div>
                ))}
              </div>
            ) : (
              <p className="font-black text-green-900 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" /> Nenhuma evasão detectada para este aluno
              </p>
            )}
          </div>
        )}
      </div>

      {/* LISTA */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-1">
              {([
                { id: 'pendentes', label: `Pendentes${stats ? ` (${stats.unresolved})` : ''}` },
                { id: 'criticos',  label: 'Críticos' },
                { id: 'todos',     label: 'Todos' },
              ] as const).map(tab => (
                <button key={tab.id} onClick={() => setAbaAtiva(tab.id)}
                  className={`px-5 py-2 rounded-lg font-bold text-sm transition-all ${
                    abaAtiva === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative md:col-span-2">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" value={busca} onChange={e => setBusca(e.target.value)}
                placeholder="Buscar por email, CPF, aluno, afiliada..."
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent text-gray-900 font-semibold" />
            </div>
            {abaAtiva === 'todos' && (<>
              <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
                className="px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 font-semibold">
                <option value="">Todos Status</option>
                {(['detected', 'confirmed', 'resolved', 'false_positive'] as EvasionStatus[]).map(s => (
                  <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                ))}
              </select>
              <select value={filtroSeverity} onChange={e => setFiltroSeverity(e.target.value)}
                className="px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 font-semibold">
                <option value="">Toda Severidade</option>
                {(['critical', 'high', 'medium', 'low'] as SeverityLevel[]).map(s => (
                  <option key={s} value={s}>{SEV_CONFIG[s].label}</option>
                ))}
              </select>
            </>)}
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-10 h-10 text-orange-500 animate-spin" />
            <p className="text-gray-500 font-semibold">Carregando registros...</p>
          </div>
        ) : evasoesFiltradas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Shield className="w-16 h-16 text-green-300 mx-auto mb-4" />
            <p className="text-gray-600 font-black text-lg">Nenhum registro encontrado</p>
            <p className="text-gray-500 text-sm mt-1">
              {abaAtiva === 'pendentes' ? '✅ Sem evasões pendentes no momento' : 'Ajuste os filtros ou rode a detecção'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {evasoesFiltradas.map(evasion => {
              const sev = SEV_CONFIG[evasion.severityLevel];
              const sta = STATUS_CONFIG[evasion.status];
              return (
                <div key={evasion.id} className="p-5 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1 min-w-0">
                      <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-2 ${sev.dot}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-black ${sev.bg} ${sev.color} border ${sev.border}`}>
                            {sev.label}
                          </span>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${sta.bg} ${sta.color}`}>
                            {sta.label}
                          </span>
                          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-700">
                            {TYPE_LABELS[evasion.evasionType]}
                          </span>
                        </div>
                        <p className="text-sm font-bold text-gray-900 mb-1">
                          {evasion.description ?? 'Sem descrição'}
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                          {evasion.detectionData?.email && <span>📧 {evasion.detectionData.email}</span>}
                          {evasion.detectionData?.cpf && <span>📄 CPF: {evasion.detectionData.cpf}</span>}
                          {evasion.detectionData?.duplicateCount && <span>👥 {evasion.detectionData.duplicateCount}x duplicado</span>}
                          {evasion.detectionData?.confidenceScore && (
                            <span>🎯 {(evasion.detectionData.confidenceScore * 100).toFixed(0)}% confiança</span>
                          )}
                          {evasion.affiliateFrom?.schoolName && <span>🏫 De: {evasion.affiliateFrom.schoolName}</span>}
                          {evasion.affiliateTo?.schoolName  && <span>→ Para: {evasion.affiliateTo.schoolName}</span>}
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(evasion.detectedAt).toLocaleString('pt-BR')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {!evasion.isResolved && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button onClick={() => setDetalhe(evasion)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Ver detalhes">
                          <Eye className="w-5 h-5" />
                        </button>
                        {evasion.status === 'detected' && (
                          <button onClick={() => handleConfirmar(evasion.id)} disabled={salvando}
                            className="px-3 py-2 rounded-lg bg-orange-600 text-white text-xs font-bold hover:bg-orange-700 disabled:opacity-60">
                            Confirmar
                          </button>
                        )}
                        <button onClick={() => { setAcao({ id: evasion.id, tipo: 'resolve' }); setResolucao(''); }}
                          className="px-3 py-2 rounded-lg bg-green-600 text-white text-xs font-bold hover:bg-green-700">
                          Resolver
                        </button>
                        <button onClick={() => { setAcao({ id: evasion.id, tipo: 'false-positive' }); setResolucao('Falso positivo confirmado'); }}
                          className="px-3 py-2 rounded-lg border-2 border-gray-300 text-gray-700 text-xs font-bold hover:bg-gray-50">
                          Falso +
                        </button>
                      </div>
                    )}
                    {evasion.isResolved && (
                      <div className="text-right text-xs text-gray-500 flex-shrink-0">
                        <CheckCircle className="w-5 h-5 text-green-500 ml-auto mb-1" />
                        {evasion.resolvedAt ? new Date(evasion.resolvedAt).toLocaleDateString('pt-BR') : '—'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* MODAL AÇÃO */}
      {acao && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-black text-gray-900 mb-4">
              {acao.tipo === 'resolve' ? '✅ Resolver Evasão' : '🔄 Marcar como Falso Positivo'}
            </h3>
            <textarea value={resolucao} onChange={e => setResolucao(e.target.value)} rows={4}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 resize-none text-gray-900 font-semibold"
              placeholder="Descreva a resolução ou justificativa..." />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setAcao(null)}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">Cancelar</button>
              <button onClick={handleResolver} disabled={salvando}
                className="flex-1 px-4 py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-60">
                {salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />} Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DETALHE */}
      {detalhe && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-black text-gray-900">Detalhes da Ocorrência</h3>
              <button onClick={() => setDetalhe(null)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              {[
                { label: 'ID',              value: detalhe.id },
                { label: 'Tipo',            value: TYPE_LABELS[detalhe.evasionType] },
                { label: 'Severidade',      value: SEV_CONFIG[detalhe.severityLevel].label },
                { label: 'Status',          value: STATUS_CONFIG[detalhe.status].label },
                { label: 'Descrição',       value: detalhe.description ?? '—' },
                { label: 'Detectado em',    value: new Date(detalhe.detectedAt).toLocaleString('pt-BR') },
                { label: 'Afiliada Origem', value: detalhe.affiliateFrom?.schoolName ?? detalhe.affiliateFromId ?? '—' },
                { label: 'Afiliada Destino',value: detalhe.affiliateTo?.schoolName  ?? detalhe.affiliateToId  ?? '—' },
              ].map(f => (
                <div key={f.label} className="flex items-start gap-3">
                  <span className="text-gray-500 w-36 flex-shrink-0 font-semibold">{f.label}</span>
                  <span className="text-gray-900 font-bold">{f.value}</span>
                </div>
              ))}
              {detalhe.detectionData && (
                <div>
                  <p className="text-gray-500 font-semibold mb-2">Dados de Detecção</p>
                  <pre className="bg-gray-50 rounded-xl p-4 text-xs text-gray-800 overflow-auto border border-gray-200">
                    {JSON.stringify(detalhe.detectionData, null, 2)}
                  </pre>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button onClick={() => setDetalhe(null)}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">Fechar</button>
              {!detalhe.isResolved && (
                <button onClick={() => { setAcao({ id: detalhe.id, tipo: 'resolve' }); setResolucao(''); setDetalhe(null); }}
                  className="flex-1 px-4 py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700">
                  Resolver
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RelatorioEvasao;